// Steering, in SerenityConsult's look: one rhythm for the year. The year as a month timeline (the annual strategy,
// the quarterly roadmap reviews, the monthly steering meetings, and the improvements started and kept each month), then
// a card for each meeting (who comes, what it decides, what it leaves with), the meetings and the open actions. Each
// meeting's pack is drafted from the data; the record (who came, notes, decisions, actions) saves as you type; once
// it's held, its pack is kept as it was.
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CommandBar } from "../Shell";
import { ErrorBox } from "../ui";
import { useLoad } from "../hooks";
import { imp, day, money, qty, type Action, type Format, type Meeting, type PackSection, type Steering } from "./impApi";
import { Head, Kpis, Win } from "./SerKit";
import "./ser.css";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MARK: Record<Meeting["kind"], string> = { strategy: "mk-a", roadmap: "mk-r", steering: "mk-s" };
const TIER: Record<Meeting["kind"], { name: string; does: string; who: string; decides: string; leaves: string }> = {
  strategy: { name: "Annual strategy", does: "Where the company spends its time and money, and the goals for the year.",
    who: "The owner or general manager, the department heads and finance", decides: "The goals for the year and which departments go first", leaves: "The year's targets and a first roadmap for each department" },
  roadmap: { name: "Quarterly roadmap review", does: "Each department's roadmap: what's next, what was kept and what to retire.",
    who: "The department heads, the improvement lead and IT", decides: "What starts next in each department, and which systems to replace or retire", leaves: "A ranked roadmap for each department" },
  steering: { name: "Monthly steering", does: "What moved, the results, the decisions waiting and the actions.",
    who: "The improvement lead, the owners of requests in the cycle and a sponsor", decides: "Keep or roll back; which opportunity starts next", leaves: "Decisions, and actions with an owner and a due date" },
};
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

export function SteeringPage() {
  const nav = useNavigate();
  const [tick, setTick] = useState(0);
  const s = useLoad(() => imp.steering(), [tick]);
  const [err, setErr] = useState<unknown>(null);
  const [note, setNote] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const d = s.data;
  const plan = async () => {
    setErr(null); setNote(null);
    try { const r = await imp.planMeetings(); setNote(r.planned ? `${r.planned} meeting${r.planned === 1 ? "" : "s"} planned. Change a date on the meeting.` : "Each meeting already has its next one planned."); setTick((t) => t + 1); } catch (x) { setErr(x); }
  };
  const late = (d?.actions ?? []).filter((a) => a.due && a.due < todayIso()).length;
  const kept = d ? d.year.months.reduce((t, m) => t + m.kept, 0) : 0;
  const keptValue = d ? d.year.months.reduce((t, m) => t + m.keptValue, 0) : 0;
  const held = d ? d.meetings.filter((m) => m.state === "held" && m.heldOn.startsWith(String(d.year.year))).length : 0;
  return (
    <>
      <CommandBar status={d ? `${d.meetings.filter((m) => m.state === "planned").length} planned · ${d.actions.length} open action${d.actions.length === 1 ? "" : "s"}` : "Loading…"}>
        <button className="cmd primary" onClick={plan}>Plan the next meetings</button>
        <button className="cmd" onClick={() => setAdding((x) => !x)} aria-pressed={adding}>New meeting</button>
        <Link className="cmd" to="/improve/roadmap">Roadmap</Link>
      </CommandBar>
      <div className="page ser">
        <Head kicker="Improvement · Steering" title="One rhythm for the year"
          lede="An annual strategy, a quarterly roadmap review and a monthly steering meeting. CourageMFG drafts each meeting's pack from the data; the people in the room decide." />
        <ErrorBox error={s.error ?? err} />
        {note && <div className="msg info">{note}</div>}
        {adding && <NewMeeting onDone={(id) => { setAdding(false); if (id) nav(`/improve/steering/${id}`); }} />}
        <Kpis items={[
          { label: "Meetings held this year", value: d ? held : "–" },
          { label: "Improvements kept", value: d ? kept : "–", sub: d ? `${money(keptValue)} a year` : "", tone: kept ? "up" : undefined },
          { label: "Open actions", value: d ? d.actions.length : "–", sub: late ? `${late} late` : "None late", tone: late ? "dn" : undefined },
        ]} />
        {d && <Year d={d} />}
        <div className="tiers">
          {(d?.cadence ?? []).map((c) => {
            const t = TIER[c.kind];
            return (
              <article key={c.kind} className="tier">
                <div className="tier-h"><h3><i className={MARK[c.kind]} aria-hidden="true" />{t.name}</h3><span className="tag">{c.every}</span></div>
                <p>{t.does}</p>
                <dl>
                  <div><dt>Who</dt><dd>{t.who}</dd></div>
                  <div><dt>Decides</dt><dd>{t.decides}</dd></div>
                  <div><dt>Leaves with</dt><dd>{t.leaves}</dd></div>
                </dl>
                <div className="when">
                  {c.last ? <Link className="mini" to={`/improve/steering/${c.last.id}`}>Last: {day(c.last.heldOn)}</Link> : <span className="pill">Not held yet</span>}
                  {c.next ? <Link className="mini p" to={`/improve/steering/${c.next.id}`}>Next: {day(c.next.heldOn)}</Link> : <span className={`pill ${c.overdue ? "a" : ""}`}>{c.overdue ? `Not planned; due by ${day(c.dueBy)}` : "Not planned"}</span>}
                </div>
              </article>);
          })}
        </div>
        <div className="s-grid2 wide-l" style={{ marginTop: 16 }}>
          <Win title="Meetings">
            {!d ? <div className="empty">Loading…</div> : !d.meetings.length ? <p className="empty">No meetings yet. <button className="mini" onClick={plan}>Plan the first of each</button></p> : (
              <div className="scroll-x"><table className="tbl">
                <thead><tr><th>Date</th><th>Meeting</th><th>Status</th><th className="r hide-m">Decisions</th><th className="r hide-m">Open actions</th></tr></thead>
                <tbody>{d.meetings.map((m) => (
                  <tr key={m.id}><td className="mono" style={{ whiteSpace: "nowrap" }}>{day(m.heldOn)}</td>
                    <td><strong><Link to={`/improve/steering/${m.id}`}>{m.title}</Link></strong><span className="sub">{m.kindName}</span></td>
                    <td>{m.state === "held" ? <span className="pill g">Held</span> : <span className="pill">Planned</span>}</td>
                    <td className="r mono hide-m">{m.decisions}</td><td className="r mono hide-m">{m.openActions}</td></tr>))}
                </tbody></table></div>)}
          </Win>
          <Win title="Open actions" sub="from held meetings">
            {!d ? <div className="empty">Loading…</div> : !d.actions.length ? <p className="empty">No open actions.</p> : (
              <ul className="alist">{d.actions.map((a) => (
                <li key={`${a.meetingId}:${a.id}`}><span>{a.text}<small>{[a.owner, a.due && `due ${day(a.due)}`].filter(Boolean).join(" · ") || "No owner"} · <Link to={`/improve/steering/${a.meetingId}`}>{a.meetingTitle}</Link></small></span>
                  {a.due && a.due < todayIso() && <span className="pill a">Late</span>}</li>))}
              </ul>)}
          </Win>
        </div>
      </div>
    </>
  );
}

/** The year as a month timeline: tap a month to see what happened in it. */
function Year({ d }: { d: Steering }) {
  const y = d.year.year;
  const now = new Date();
  const nowM = now.getFullYear() === y ? now.getMonth() : -1;
  const [sel, setSel] = useState(nowM >= 0 ? nowM : 0);
  const inYear = d.meetings.filter((m) => m.heldOn.startsWith(`${y}-`));
  const monthOf = (iso: string) => Number(iso.slice(5, 7)) - 1;
  const meet = (k: Meeting["kind"], mo: number) => inYear.filter((m) => m.kind === k && monthOf(m.heldOn) === mo);
  const cls = (mo: number) => `${mo === sel ? "sel" : ""} ${mo === nowM ? "now" : ""}`;
  const row = (k: Meeting["kind"]) => (
    <div className="yr-row" key={k}>
      <span className="yr-l">{TIER[k].name}<small>{k === "strategy" ? "Once a year" : k === "roadmap" ? "Each quarter" : "Each month"}</small></span>
      <div className="yr-t" aria-hidden="true">{MONTHS.map((_, mo) => {
        const ms = meet(k, mo);
        return <span key={mo} className={`${cls(mo)} ${ms.length && ms.every((m) => m.state === "planned") ? "planned" : ""}`}>{ms.length > 0 && <i className={MARK[k]} />}</span>;
      })}</div>
    </div>);
  const month = d.year.months.find((m) => m.month === sel + 1) ?? { month: sel + 1, started: 0, kept: 0, keptValue: 0 };
  const there = inYear.filter((m) => monthOf(m.heldOn) === sel);
  return (
    <Win title="The year" sub="tap a month" tag={String(y)}>
      <div className="yr">
        <div className="yr-row">
          <span className="yr-l" />
          <div className="yr-t head" role="group" aria-label="Month">{MONTHS.map((m, mo) => <span key={m}><button aria-pressed={mo === sel} onClick={() => setSel(mo)} aria-label={new Date(y, mo, 1).toLocaleDateString("en-US", { month: "long" })}><span className="hide-m">{m}</span><span className="show-m">{m[0]}</span></button></span>)}</div>
        </div>
        {row("strategy")}{row("roadmap")}{row("steering")}
        <div className="yr-row">
          <span className="yr-l">Improvements<small>Started · kept</small></span>
          <div className="yr-t" aria-hidden="true">{MONTHS.map((_, mo) => {
            const m = d.year.months.find((x) => x.month === mo + 1);
            return <span key={mo} className={cls(mo)}>{m && (m.started || m.kept) ? <span className="yr-n">{m.started}·{m.kept}</span> : ""}</span>;
          })}</div>
        </div>
      </div>
      <div className="yr-info" aria-live="polite">
        <b>{new Date(y, sel, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" })}</b>
        {there.length ? there.map((m) => <span key={m.id}><Link to={`/improve/steering/${m.id}`}>{m.title}</Link> · {m.state === "held" ? `held ${day(m.heldOn)}, ${m.decisions} decision${m.decisions === 1 ? "" : "s"}` : `planned for ${day(m.heldOn)}`}</span>) : <span>No meeting this month.</span>}
        <span>{month.started} improvement{month.started === 1 ? "" : "s"} started · {month.kept} kept{month.kept ? `, ${money(month.keptValue)} a year` : ""}</span>
      </div>
      <p className="note"><i className="mk-a" style={{ width: 10, height: 10, verticalAlign: "middle", marginRight: 4 }} />Strategy · <i className="mk-r" style={{ width: 10, height: 10, verticalAlign: "middle", marginRight: 4 }} />Roadmap review · <i className="mk-s" style={{ verticalAlign: "middle", marginRight: 4 }} />Steering. An open mark is planned, not held yet.</p>
    </Win>
  );
}

function NewMeeting({ onDone }: { onDone: (id: string | null) => void }) {
  const [b, setB] = useState({ kind: "steering", heldOn: todayIso(), title: "" });
  const [err, setErr] = useState<unknown>(null);
  const save = async (e: FormEvent) => { e.preventDefault(); setErr(null); try { const m = await imp.createMeeting({ kind: b.kind, heldOn: b.heldOn, title: b.title || undefined }); onDone(m.id); } catch (x) { setErr(x); } };
  return (
    <div style={{ marginBottom: 16 }}>
      <Win title="New meeting">
        <form className="win-body" onSubmit={save}>
          <div className="form-grid">
            <div className="field"><label htmlFor="nm-k">Kind</label><select id="nm-k" className="select" value={b.kind} onChange={(e) => setB({ ...b, kind: e.target.value })}>
              <option value="steering">Monthly steering</option><option value="roadmap">Quarterly roadmap review</option><option value="strategy">Annual strategy</option></select></div>
            <div className="field"><label htmlFor="nm-d">Date</label><input id="nm-d" className="input" type="date" required value={b.heldOn} onChange={(e) => setB({ ...b, heldOn: e.target.value })} /></div>
            <div className="field"><label htmlFor="nm-t">Title</label><input id="nm-t" className="input" placeholder="Named from its kind and date" value={b.title} onChange={(e) => setB({ ...b, title: e.target.value })} /></div>
          </div>
          <ErrorBox error={err} />
          <div className="btn-row"><button className="s-btn p">Plan the meeting</button><button type="button" className="s-btn" onClick={() => onDone(null)}>Cancel</button></div>
        </form>
      </Win>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// One meeting
// ---------------------------------------------------------------------------------------------------------------
type Rec = Pick<Meeting, "heldOn" | "attendees" | "notes" | "decisions" | "actions">;

export function MeetingPage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const [tick, setTick] = useState(0);
  const r = useLoad(() => imp.meeting(id), [id, tick]);
  const [err, setErr] = useState<unknown>(null);
  const m = r.data;
  if (r.error) return <div className="page ser"><ErrorBox error={r.error} /><Link to="/improve/steering">Back to steering</Link></div>;
  if (!m) return <div className="page ser"><div className="empty">Loading…</div></div>;
  const hold = async () => { setErr(null); try { await imp.holdMeeting(m.id); setTick((t) => t + 1); } catch (x) { setErr(x); } };
  const remove = async () => { if (!confirm(`Delete ${m.title}?`)) return; setErr(null); try { await imp.deleteMeeting(m.id); nav("/improve/steering"); } catch (x) { setErr(x); } };
  const t = TIER[m.kind];
  return (
    <>
      <CommandBar status={m.state === "held" ? `Held ${day(m.heldOn)}${m.heldByName ? ` · ${m.heldByName}` : ""} · the pack as it was` : `Planned for ${day(m.heldOn)} · the pack is drafted each time it's opened`}>
        {m.state === "planned" && <button className="cmd primary" onClick={hold} disabled={m.heldOn > todayIso()} title={m.heldOn > todayIso() ? "Mark it held on the day" : undefined}>Mark held</button>}
        {m.state === "planned" && <button className="cmd" onClick={() => setTick((x) => x + 1)}>Draft the pack again</button>}
        <button className="cmd" onClick={() => window.print()}>Print</button>
        <Link className="cmd" to="/improve/steering">All meetings</Link>
        {m.state === "planned" && <button className="cmd" onClick={remove}>Delete</button>}
      </CommandBar>
      <div className="page ser">
        <Head kicker={<><i className={MARK[m.kind]} aria-hidden="true" style={{ width: 10, height: 10, marginRight: 8, verticalAlign: "middle" }} />{m.kindName} · {day(m.heldOn)}{m.pack ? ` · since ${day(m.pack.since)}` : ""}</>} title={m.title}
          lede={<><b>Decides:</b> {t.decides.toLowerCase()}. <b>Leaves with:</b> {t.leaves.toLowerCase()}.</>} />
        <ErrorBox error={err} />
        <div className="s-grid2 wide-l">
          <div className="s-stack">{(m.pack?.sections ?? []).map((s, i) => <PackCard key={i} s={s} />)}</div>
          <div className="s-stack"><RecordCard m={m} /></div>
        </div>
      </div>
    </>
  );
}

function cellText(v: string | number | null, f: Format) {
  if (v === null || v === undefined || v === "") return <span className="muted">–</span>;
  if (f === "usd") return money(Number(v));
  if (f === "int" || f === "num") return qty(Number(v));
  if (f === "date") return day(String(v));
  if (f === "change") { const x = Number(v); return Math.round(x) === 0 ? <span className="muted">–</span> : x < 0 ? <span className="saved">{money(-x)} less</span> : <span className="more">{money(x)} more</span>; }
  return String(v);
}
const right = (f: Format) => f === "usd" || f === "int" || f === "num" || f === "change";
const plain = (v: string | number | null, f: Format) => (v === null ? "–" : f === "usd" ? money(Number(v)) : f === "int" || f === "num" ? qty(Number(v)) : f === "date" ? day(String(v)) : String(v));

function PackCard({ s }: { s: PackSection }) {
  const rows = s.table?.rows ?? [];
  return (
    <Win title={s.title}>
      {s.note && <p className="muted" style={{ margin: 0, padding: "12px 16px 0", fontSize: 14 }}>{s.note}</p>}
      {s.numbers && <Kpis boxed={false} items={s.numbers.map((x) => ({ label: x.label, value: plain(x.value, x.format), sub: x.sub }))} />}
      {s.table && !rows.length && <p className="empty">{s.empty ?? "Nothing here."}</p>}
      {s.table && rows.length > 0 && (
        <div className="scroll-x" style={s.numbers ? { borderTop: "1px solid var(--s-border)" } : undefined}><table className="tbl">
          <thead><tr>{s.table.columns.map((c, i) => <th key={i} className={`${right(c.format) ? "r" : ""} ${i > 2 ? "hide-m" : ""}`}>{c.label}</th>)}</tr></thead>
          <tbody>{rows.map((row, ri) => (
            <tr key={ri}>{row.map((v, ci) => {
              const f = s.table!.columns[ci].format, link = ci === 0 ? s.table!.links?.[ri] : null;
              return <td key={ci} className={`${right(f) ? "r mono" : ""} ${ci > 2 ? "hide-m" : ""}`}>{link ? <strong><Link to={link}>{cellText(v, f)}</Link></strong> : cellText(v, f)}</td>;
            })}</tr>))}
          </tbody>
        </table></div>)}
    </Win>
  );
}

/** The meeting's record: who came, notes, decisions and actions. Saves as you type; only what changed is sent. */
function RecordCard({ m }: { m: Meeting }) {
  const [rec, setRec] = useState<Rec>({ heldOn: m.heldOn, attendees: m.attendees, notes: m.notes, decisions: m.decisions, actions: m.actions });
  const [status, setStatus] = useState<{ text: string; bad?: boolean }>({ text: "All changes saved" });
  const saved = useRef<Rec>(rec);
  const timer = useRef<number | null>(null);
  useEffect(() => { const r = { heldOn: m.heldOn, attendees: m.attendees, notes: m.notes, decisions: m.decisions, actions: m.actions }; setRec(r); saved.current = r; }, [m.id, m.state]);
  const change = (next: Rec) => {
    setRec(next);
    if (timer.current) window.clearTimeout(timer.current);
    setStatus({ text: "Saving…" });
    timer.current = window.setTimeout(async () => {
      const diff = Object.fromEntries((Object.keys(next) as (keyof Rec)[]).filter((k) => JSON.stringify(next[k]) !== JSON.stringify(saved.current[k])).map((k) => [k, next[k]])) as Partial<Rec>;
      if (diff.decisions) diff.decisions = diff.decisions.filter((x) => x.trim());
      if (diff.actions) diff.actions = diff.actions.filter((a) => a.text.trim());
      if (!Object.keys(diff).length) { setStatus({ text: "All changes saved" }); return; }
      try { await imp.updateMeeting(m.id, diff); saved.current = { ...saved.current, ...diff }; setStatus({ text: "All changes saved" }); }
      catch (x: any) { setStatus({ text: `Not saved: ${x?.message ?? "the server refused it"}`, bad: true }); }
    }, 700);
  };
  const set = <K extends keyof Rec>(k: K, v: Rec[K]) => change({ ...rec, [k]: v });
  const setAction = (i: number, a: Partial<Action>) => set("actions", rec.actions.map((x, j) => (j === i ? { ...x, ...a } : x)));
  const held = m.state === "held";
  return (
    <Win title="The meeting" actions={<span className={`rec-status ${status.bad ? "bad" : ""}`} role="status">{status.text}</span>}>
      <div className="win-body">
        {!held && <div className="field"><label htmlFor="mt-d">Date</label><input id="mt-d" className="input" type="date" value={rec.heldOn} onChange={(e) => e.target.value && set("heldOn", e.target.value)} /></div>}
        <div className="field"><label htmlFor="mt-a">Who came</label><input id="mt-a" className="input" placeholder="Names, with commas" value={rec.attendees} onChange={(e) => set("attendees", e.target.value)} /></div>
        <div className="field"><label htmlFor="mt-n">Notes</label><textarea id="mt-n" className="textarea" rows={6} value={rec.notes} onChange={(e) => set("notes", e.target.value)} /></div>
        <div className="field"><span className="label" style={{ fontWeight: 600 }}>Decisions</span>
          {rec.decisions.map((x, i) => (
            <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              <input className="input" aria-label={`Decision ${i + 1}`} value={x} onChange={(e) => set("decisions", rec.decisions.map((y, j) => (j === i ? e.target.value : y)))} />
              <button type="button" className="x" aria-label={`Remove decision ${i + 1}`} onClick={() => set("decisions", rec.decisions.filter((_, j) => j !== i))}>×</button>
            </div>))}
          <button type="button" className="mini" onClick={() => setRec({ ...rec, decisions: [...rec.decisions, ""] })}>Add a decision</button>
        </div>
        <div className="field" style={{ marginBottom: 0 }}><span className="label" style={{ fontWeight: 600 }}>Actions</span>
          {rec.actions.map((a, i) => (
            <div key={a.id || i} className="act-row">
              <input type="checkbox" aria-label={`Done: ${a.text}`} checked={a.done} onChange={(e) => setAction(i, { done: e.target.checked })} />
              <input className={`input ${a.done ? "done" : ""}`} aria-label={`Action ${i + 1}`} placeholder="What" value={a.text} onChange={(e) => setAction(i, { text: e.target.value })} />
              <input className="input hide-m" aria-label={`Owner of action ${i + 1}`} placeholder="Who" value={a.owner} onChange={(e) => setAction(i, { owner: e.target.value })} />
              <input className="input hide-m" type="date" aria-label={`Due date of action ${i + 1}`} value={a.due ?? ""} onChange={(e) => setAction(i, { due: e.target.value || null })} />
              <button type="button" className="x" aria-label={`Remove action ${i + 1}`} onClick={() => set("actions", rec.actions.filter((_, j) => j !== i))}>×</button>
            </div>))}
          <button type="button" className="mini" onClick={() => setRec({ ...rec, actions: [...rec.actions, { id: crypto.randomUUID(), text: "", owner: "", due: null, done: false }] })}>Add an action</button>
        </div>
      </div>
    </Win>
  );
}
